import React from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import RightIcon from '@material-ui/icons/KeyboardArrowRight'
import LeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import ImageCarousel from './ImageCarousel'
import icons from '../enums/icons'
import { getPlaceholderImageUrl } from '../enums/itemTypePlaceholderImages'
import Tags from './Tags'
import getField from '../utilities/Data/getField'
import imageUpload from '../utilities/imageUpload'
import debounce from '../utilities/debounce'
import connectPropTypes from '../utilities/connectPropTypes'
import downloadImages from '../utilities/downloadImages'
import { kitItemTemplates, templateNames } from '../utilities/kitItemTemplates'
import createFlatLayImage from '../utilities/Flatlay/createFlatlayImage'
import styles from './KitImage.icss'
import Item from '../utilities/Data/Item'

const logoSettings = { x: 50, y: 93, s: 20, a: 0.5, b: false }

//	Ref: https://xkcd.com/1425/ (don't add any more features!)

const getCanvasDims = () => {
	//	Ensure we have a square canvas with PX width/height and correct scaling
	//	ref: https://stackoverflow.com/a/29880929/6637365
	const rect = document.getElementById('canvasSurround').getBoundingClientRect()

	return {
		width: rect.width * global.devicePixelRatio,
		height: rect.height * global.devicePixelRatio,
		viewWidth: rect.width,
		viewHeight: rect.height
	}
}

//	Get the images and filter on type - we use half, to ensure a decent size and reasonable speed of rendering.
const grabImages = (kitItems, imgType = 'half') => kitItems.map(item => ({
	image: getField(item, 'itemImages').filter(i => i.type === imgType)[0],
	brandKey: getField(item, 'item.brandKey'),
	itemType: getField(item, 'item.itemType') || getField(item, 'itemType')
}))

const grabTemplate = (displayImages, templateName = templateNames.cadelEvans) => {
	const minDisplayImages = Math.min(...Object.keys(kitItemTemplates))
	const templates = kitItemTemplates[displayImages] || kitItemTemplates[minDisplayImages]
	const template = templates.filter(t => t.name === templateName)[0]
	return template
}

const getListOfImageStates = (itemCount) => {
	let states = []

	Object.keys(kitItemTemplates).forEach((num) => {
		if (num <= itemCount) {
			kitItemTemplates[num].map((t) => {
				const displayImages = num
				const templateName = t.name
				return states.push({ displayImages, templateName })
			})
		}
	})

	//	Show biggest templates first
	states = states.reverse()
	return states
}

class KitImage extends React.Component {
	constructor(props) {
		super(props)
		this.state = {
			selectedItem: 0,
			isRenderingFlatlay: !!(props.kitItems && props.kitItems.length > 0),
			tagsAreHidden: true
		}

		this.renderedCanvases = {}

		this.shouldRenderFlatlay = this.shouldRenderFlatlay.bind(this)
		this.renderFlatlay = this.renderFlatlay.bind(this)
		this.renderCanvases = debounce(this.renderCanvases.bind(this), 500)
		this.getSelectedItem = this.getSelectedItem.bind(this)
		this.cycleBack = this.cycleBack.bind(this)
		this.cycleForward = this.cycleForward.bind(this)
		this.defaultPositionUpdates = this.defaultPositionUpdates.bind(this)
		this.templateUpdate = this.templateUpdate.bind(this)
		this.handleCarouselChange = this.handleCarouselChange.bind(this)
		this.overrideTagsHide = this.overrideTagsHide.bind(this)
		this.fixCanvasDPI = this.fixCanvasDPI.bind(this)

		this.canvasRefs = []
		this.addRef = index => (canvas) => {
			if (canvas) {
				this.canvasRefs[index] = canvas
			} else {
				this.canvasRefs.splice(index, 1)
			}
		}
		this.clearRefs = () => {
			this.canvasRefs = []
		}
	}

	componentDidMount() {
		this.mounted = true
		if (this.props.onCompleteBinder) {
			this.props.onCompleteBinder((kitId, cb) => {
				const selectedItem = this.getSelectedItem()
				const imageStates = getListOfImageStates(this.props.kitItems.length)
				const templateSettings = imageStates[selectedItem]
				const myTemplate = grabTemplate(templateSettings.displayImages, templateSettings.templateName)
				const can = global.document.createElement('canvas')
				can.width = 1080
				can.height = 1080
				let kitImages = grabImages(this.props.kitItems).filter((img, index) => index < templateSettings.displayImages)

				kitImages.push({ image: { url: icons.brandKitfitShadow } })
				kitImages = kitImages.map(img => (img && img.image ? img : { image: { url: getPlaceholderImageUrl(img.itemType) } }))
				const imageOptions = kitImages.map(img => ({ brandKey: getField(img, 'brandKey') }))

				//	Note: The browser takes care of managing urls being downloaded in parallel - only 1 request per image goes out.
				downloadImages({ imageUrls: kitImages.map(img => getField(img, 'image.url')), imageOptions, skipCache: true }).then((imageInstances) => {
					const template = myTemplate.entries.slice()
					template.push(logoSettings)

					createFlatLayImage({
						canvas: can,
						imageInstances,
						template,
						imageOptions,
						callback: () => {
							can.toBlob((blob) => {
								const onSuccess = (url) => {
									if (cb) {
										cb(url)
									}
								}
								imageUpload({ blob, onSuccess, fileName: `kit-${kitId}.jpg` })
							}, 'image/jpeg', 0.8)
						}
					})
				})
			})
		}

		const selectedItem = this.getSelectedItem()

		this.setState({ selectedItem, isRenderingFlatlay: !!(this.props.kitItems && this.props.kitItems.length > 0) }, () => {
			this.renderCanvases(selectedItem)
		})
	}

	componentDidUpdate(prevProps) {
		const isEqual = (a, b) => a.length === b.length && a.every((value, index) => {
			const hasDifferingAlgoliaIndex = value.item.algoliaIndexUpdated !== b[index].item.algoliaIndexUpdated
			return value.itemKey === b[index].itemKey && value.colourName === b[index].colourName && !hasDifferingAlgoliaIndex
		})

		if (!isEqual(this.props.kitItems, prevProps.kitItems)) {
			this.renderedCanvases = {}
		}

		const hasItems = !!(this.props.kitItems && this.props.kitItems.length > 0)
		if (hasItems) {
			const selectedItem = this.getSelectedItem()
			if (selectedItem !== this.state.prevSelectedItem || !isEqual(this.props.kitItems, prevProps.kitItems)) {
				const prevSelectedItem = selectedItem
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({ selectedItem, prevSelectedItem, isRenderingFlatlay: this.shouldRenderFlatlay(selectedItem) }, () => {
					this.renderCanvases(selectedItem)
				})
			}
		}
	}

	componentWillUnmount() {
		this.mounted = false
		clearTimeout(this.flatlayTimer)
	}


	getSelectedItem() {
		//	If the kit has templateSettings, use that to set selectedItem
		const templateSettings = this.props.kitTemplateSettings
		let selectedItem = 0

		if (templateSettings) {
			const imageStates = getListOfImageStates(this.props.kitItems.length)
			imageStates.forEach((is, i) => {
				if (is.displayImages === templateSettings.displayImages && is.templateName === templateSettings.templateName) {
					selectedItem = i
				}
			})
		}

		return selectedItem
	}

	fixCanvasDPI(canvas) {
		if (this.mounted) {
			//	Ensure we have a square canvas with PX width/height and correct scaling
			//	ref: https://stackoverflow.com/a/29880929/6637365
			const canvasDims = getCanvasDims()

			canvas.width = canvasDims.width	// eslint-disable-line no-param-reassign
			canvas.height = canvasDims.height // eslint-disable-line no-param-reassign
			canvas.style.width = `${canvasDims.viewWidth}px`	// eslint-disable-line no-param-reassign
			canvas.style.height = `${canvasDims.viewHeight}px`	// eslint-disable-line no-param-reassign

			const ctx = canvas.getContext('2d')
			ctx.scale(global.devicePixelRatio, global.devicePixelRatio)
		}
	}

	shouldRenderFlatlay(selectedItem, index) {
		const hasItems = !!(this.props.kitItems && this.props.kitItems.length > 0)
		if (!hasItems || !this.mounted) {
			return false
		}
		let shouldRender = false
		const renderIndexes = selectedItem === 0 ? [0, 1] : [(selectedItem - 1), selectedItem, (selectedItem + 1)]
		const imageStates = getListOfImageStates(this.props.kitItems.length)
		if (typeof index !== 'undefined') {
			const state = imageStates[index]
			if (state) {
				const renderKey = `${index}-${state.displayImages}-${state.templateName}-${this.props.kitItems.map(ki => `${ki.itemKey}-${ki.colourName}`)}`
				if (renderIndexes.indexOf(index) !== -1 && !this.renderedCanvases[renderKey]) {
					shouldRender = true
				}
			}
		} else {
			//	Check if any of the selected indexes have not been rendered
			renderIndexes.forEach((rIndex) => {
				const state = imageStates[rIndex]
				if (state) {
					const renderKey = `${rIndex}-${state.displayImages}-${state.templateName}-${this.props.kitItems.map(ki => `${ki.itemKey}-${ki.colourName}`)}`
					if (renderIndexes.indexOf(rIndex) !== -1 && !this.renderedCanvases[renderKey]) {
						shouldRender = true
					}
				}
			})
		}
		return shouldRender
	}

	cycleBack() {
		this.setState((state) => {
			let selectedItem = state.selectedItem - 1
			if (selectedItem < 0) {
				selectedItem = 0
			}
			if (this.slider) {
				this.slider.slickGoTo(selectedItem)
			}
			return { selectedItem }
		})
	}

	cycleForward() {
		this.setState((state) => {
			const maxSelectedImage = getListOfImageStates(this.props.kitItems.length).length - 1
			let selectedItem = state.selectedItem + 1
			if (selectedItem > maxSelectedImage) {
				selectedItem = maxSelectedImage
			}
			if (this.slider) {
				this.slider.slickGoTo(selectedItem)
			}
			return { selectedItem }
		})
	}

	defaultPositionUpdates(template) {
		if (template && template.entries) {
			const defaultTagPositions = template.entries.map(e => ({ x: e.x, y: e.y }))
			this.setState({ defaultTagPositions }, () => {
				const pos = this.state.defaultTagPositions
				if (pos) {
					//	Update tags so the default positions are used, and tags are marked as visible
					const updatedTags = this.props.kitTags.map((tag, index) => {
						const newTag = Object.assign({}, tag)
						if (pos[index]) {
							newTag.x = pos[index].x
							delete newTag.hidden
						} else {
							delete newTag.x
							newTag.hidden = true
						}

						if (pos[index]) {
							newTag.y = pos[index].y
							delete newTag.hidden
						} else {
							delete newTag.y
							newTag.hidden = true
						}
						return newTag
					})

					this.props.handleTagsUpdate(updatedTags)
				}
			})
		}
	}

	templateUpdate(templateSettings, template) {
		this.defaultPositionUpdates(template)
		this.props.handleTemplateUpdate(templateSettings)
	}

	handleCarouselChange(selectedItem) {
		this.setState({ selectedItem })
		clearTimeout(this.flatlayTimer)
		//	Use timer to debounce updates, when looking through the sldies of kits
		this.flatlayTimer = setTimeout(() => {
			const imageStates = getListOfImageStates(this.props.kitItems.length)
			const templateSettings = imageStates[selectedItem]
			const myTemplate = grabTemplate(templateSettings.displayImages, templateSettings.templateName)
			this.templateUpdate(templateSettings, myTemplate)
		}, 250)
	}

	overrideTagsHide() {
		// eslint-disable-next-line react/no-access-state-in-setstate
		const tagsAreHidden = !this.state.tagsAreHidden
		this.setState({ tagsAreHidden })
		return tagsAreHidden
	}

	renderFlatlay(displayImages, templateName = templateNames.cadelEvans, canvas, callback) {
		let kitImages = grabImages(this.props.kitItems).filter((img, index) => index < displayImages)
		const myTemplate = grabTemplate(displayImages, templateName)

		kitImages.push({
			image: {
				url: icons.brandKitfitShadow
			}
		})
		kitImages = kitImages.map(img => (img && img.image ? img : { image: { url: getPlaceholderImageUrl(img.itemType) } }))
		const imageOptions = kitImages.map(img => ({ brandKey: getField(img, 'brandKey') }))

		//	Note: The browser takes care of managing urls being downloaded in parallel - only 1 request per image goes out.
		downloadImages({ imageUrls: kitImages.map(img => getField(img, 'image.url')), maxSquare: 512, imageOptions }).then((imageInstances) => {
			const template = myTemplate.entries.slice()
			template.push(logoSettings)
			const canvasDims = getCanvasDims()
			if (this.mounted) {
				createFlatLayImage({ canvas, imageInstances, imageOptions, fixCanvasDPI: this.fixCanvasDPI, template, callback, canvasWidth: canvasDims.width, canvasHeight: canvasDims.height })
			}
		})
	}

	renderCanvases(selectedItem) {
		if (!this.mounted) {
			return
		}
		const hasItems = !!(this.props.kitItems && this.props.kitItems.length > 0)
		if (hasItems) {
			this.canvasRefs.forEach((ref, index) => {
				if (ref) {
					const imageStates = getListOfImageStates(this.props.kitItems.length)
					const state = imageStates[index]
					const renderKey = `${index}-${state.displayImages}-${state.templateName}-${this.props.kitItems.map(ki => `${ki.itemKey}-${ki.colourName}`)}`
					if (state && this.shouldRenderFlatlay(selectedItem, index)) {
						this.renderFlatlay(state.displayImages, state.templateName, ref, () => {
							this.setState(() => ({ isRenderingFlatlay: false }))
							this.renderedCanvases[renderKey] = true
						})
					}
				}
			})
			if (typeof selectedItem !== 'undefined') {
				const imageStates = getListOfImageStates(this.props.kitItems.length)
				const state = imageStates[selectedItem]
				if (state && state.displayImages) {
					const template = grabTemplate(state.displayImages, state.templateName)
					if (template) {
						this.defaultPositionUpdates(template)
					}
				}
			}
		}
	}

	render() {
		const hasItems = !!(this.props.kitItems && this.props.kitItems.length > 0)
		const imageStates = hasItems ? getListOfImageStates(this.props.kitItems.length) : []
		this.clearRefs()
		const selectedItem = hasItems ? this.getSelectedItem() : undefined
		const shouldShowProgress = hasItems && this.state.isRenderingFlatlay && this.shouldRenderFlatlay(selectedItem, selectedItem)
		return (
			<div className={styles.kitImageBuilder}>
				<Tags
					tags={this.props.kitItems && this.props.kitItems.length !== 0 ? this.props.kitTags : undefined}
					updateTags={this.props.handleTagsUpdate}
					defaultTagPositions={this.state.defaultTagPositions}
					//	Return true if a tag should be shown
					isTagVisible={(tag) => {
						const includedTagCount = this.state.defaultTagPositions ? this.state.defaultTagPositions.length : this.props.kitItems.length
						const itemsIncluded = this.props.kitItems.filter((item, iIndex) => iIndex < includedTagCount)
						const result = !!(itemsIncluded.filter(item => Item.isSameItem(item, tag)).length > 0)
						return result
					}}
					hideTags={shouldShowProgress || this.state.tagsAreHidden}
					overrideTagsHide={this.overrideTagsHide}
				>
					<div id="canvasSurround" className={styles.kitImage}>
						{hasItems && (
							<ImageCarousel
								getRef={(slider) => { this.slider = slider }}
								className={classnames(styles.imageCarousel, (shouldShowProgress ? styles.imageCarouselLoading : undefined))}
								dots={false}
								autoplay={false}
								infinite={false}
								initialSlide={this.state.selectedItem}
								onChange={this.handleCarouselChange}
							>
								{imageStates.map((state, i) => (
									<div key={`can-${JSON.stringify(state)}`} className={styles.imageSurround}>
										<canvas ref={this.addRef(i)} className={classnames(styles.canvas, styles.image)} />
									</div>
								))}
							</ImageCarousel>
						)}
						{shouldShowProgress && (
							<div className={styles.kitImageSpinner}>
								<div className={styles.kitImageSpinnerBox}>
									<CircularProgress className={styles.progress} />
									<p>{this.context.translate('Creating your flatlay preview')}</p>
								</div>
							</div>
						)}
						{!hasItems && (
							<div className={styles.hintMessage}>
								{this.props.hintMessage ? this.props.hintMessage : (
									<div className={styles.hintMessageBox}>
										<p>{this.context.translate('Hint: Add each item of kit below e.g: Jersey, bib-short, socks, helmet, shoes, sunglasses.')}</p>
									</div>
								)}
							</div>
						)}
					</div>
				</Tags>
				{imageStates.length > 1 && (
					<div className={styles.kitImageSelector}>
						<button type="button" className={styles.bareButton} onClick={this.cycleBack}><LeftIcon /></button>
						<button type="button" className={styles.bareButton} onClick={this.cycleForward}>{this.context.translate('Choose a different layout')}</button>
						<button type="button" className={styles.bareButton} onClick={this.cycleForward}><RightIcon /></button>
					</div>
				)}
				{this.props.showKitCountMessage && (
					<div className={styles.kitCountMessage}>
						<div>{this.context.translate('{itemCount} Items in this Kit', { itemCount: this.props.kitItems ? this.props.kitItems.length : 0 })}</div>
					</div>
				)}
			</div>
		)
	}
}

KitImage.contextTypes = {
	translate: PropTypes.func
}

KitImage.propTypes = {
	onCompleteBinder: PropTypes.func,
	kitItems: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	kitTags: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	kitTemplateSettings: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	handleTemplateUpdate: PropTypes.func.isRequired,
	handleTagsUpdate: PropTypes.func.isRequired,
	showKitCountMessage: PropTypes.bool,
	hintMessage: PropTypes.node
}

KitImage.defaultProps = {
	onCompleteBinder: undefined,
	kitItems: undefined,
	kitTags: undefined,
	kitTemplateSettings: undefined,
	showKitCountMessage: false,
	hintMessage: undefined
}


export default connectPropTypes(KitImage)



// WEBPACK FOOTER //
// ./src/components/KitImage.jsx